import { useEffect, useState } from "react";

import useData from "./useData";

const useProject = (projectId) => {
  const { getFromServer, executeAction, addToWorkQueue, sortByFields } =
    useData();

  const [project, setProject] = useState(null);
  const [projectInfo, setProjectInfo] = useState(null);
  const [projects, setProjects] = useState(null);
  const [projectLocations, setProjectLocations] = useState(null);
  const [projectReps, setProjectReps] = useState(null);
  const [projectBanners, setProjectBanners] = useState(null);
  const [projectTerritories, setProjectTerritories] = useState(null);
  const [locationCount, setLocationCount] = useState(0);

  useEffect(() => {
    if (projectId) getProject(projectId);
  }, [projectId]);

  const getProject = async (id) => {
    const results = await getFromServer({
      entityName: "Project",
      id: id,
    });

    if (results) {
      setProject(results);
      return results;
    }

    setProject(null);
    return null;
  };

  const getProjectInfo = async () => {
    const results = await executeAction({
      entityName: "Project",
      action: "getProjectInfo",
      data: {
        projectId: project.id,
      },
    });

    setProjectInfo(results?.isSuccessful ? results.data : null);
  };

  const getProjects = async (filter) => {
    setProjects(null);
    const results = await getFromServer({
      entityName: "Project",
      filter,
    });

    if (Array.isArray(results)) {
      setProjects(() => {
        return sortByFields(results, ["startsOn"]);
      });
    } else {
      setProjects([]);
    }
  };

  const scheduleBasedOnFrequency = async (startOn) => {
    await addToWorkQueue("scheduleBasedOnFrequency", {
      projectId: project.id,
      startOn,
    });
  };

  const getProjectLocations = async (filter, pagination) => {
    setLocationCount(0);

    let data = {
      projectId,
      filter,
    };

    if (pagination) {
      data = { ...data, ...pagination };
    }

    const results = await executeAction({
      entityName: "ProjectLocation",
      action: "getFullList",
      data,
    });

    if (results?.isSuccessful && Array.isArray(results.data?.data)) {
      setProjectLocations(
        sortByFields(results.data.data, ["province", "territory"]),
      );

      setLocationCount(results.data.count || 0);
    } else {
      setLocationCount(0);
      setProjectLocations([]);
    }
  };

  const getNextLocations = async (filter, pagination) => {
    let data = {
      projectId,
      filter,
    };
    if (pagination) {
      data = { ...data, ...pagination };
    }

    const results = await executeAction({
      entityName: "ProjectLocation",
      action: "getFullList",
      data,
    });

    if (results?.isSuccessful && Array.isArray(results.data?.data)) {
      setProjectLocations((current) => {
        let locations = [];

        if (Array.isArray(current)) {
          locations = [...current, ...results.data.data];
        } else {
          locations = [...results.data.data];
        }

        return sortByFields(locations, ["province", "territory"]);
      });
    }
  };

  const getProjectReps = async () => {
    const results = await executeAction({
      entityName: "Call",
      action: "getScheduledReps",
      data: {
        projectId,
      },
    });

    if (results?.isSuccessful && Array.isArray(results.data)) {
      setProjectReps(sortByFields(results.data, ["name"]));
    } else {
      setProjectReps([]);
    }
  };

  const getProjectBanners = async (id) => {
    const results = await executeAction({
      entityName: "Project",
      action: "getProjectBanners",
      data: {
        projectId: id || projectId,
      },
    });

    if (results?.isSuccessful && Array.isArray(results.data)) {
      setProjectBanners(sortByFields(results.data, ["name"]));
    } else {
      setProjectBanners([]);
    }
  };

  const getProjectTerritories = async (id) => {
    const results = await executeAction({
      entityName: "Project",
      action: "getProjectTerritories",
      data: {
        projectId: id || projectId,
      },
    });

    if (results?.isSuccessful && Array.isArray(results.data)) {
      setProjectTerritories(sortByFields(results.data, ["name"]));
    } else {
      setProjectTerritories([]);
    }
  };

  const removeProjectLocations = async () => {
    const results = await executeAction({
      entityName: "ProjectLocation",
      action: "removeProjectLocations",
      data: {
        projectId,
      },
      displayMessage: true,
    });

    return results;
  };

  const removeTerritories = async () => {
    const results = await executeAction({
      entityName: "Project",
      action: "removeTerritories",
      data: {
        projectId,
      },
    });

    return results;
  };

  const removeCalls = async (id) => {
    const results = await executeAction({
      entityName: "Project",
      action: "removeCalls",
      data: {
        projectId: id || projectId,
      },
    });

    return results;
  };

  return {
    project,
    getProject,
    getProjectInfo,
    projectInfo,
    scheduleBasedOnFrequency,
    getProjectLocations,
    getNextLocations,
    locationCount,
    projectLocations,
    getProjectReps,
    projectReps,
    getProjectBanners,
    projectBanners,
    getProjects,
    projects,
    removeProjectLocations,
    removeTerritories,
    getProjectTerritories,
    projectTerritories,
    removeCalls,
  };
};

export default useProject;
